import classNames from 'classnames';
import tableStyle from '../../../row.module.css';
import { Controller, useForm } from 'react-hook-form';
import { InputWrapper } from '../../../common/input-wrapper';
import React, { PropsWithChildren, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { RouterInput, trpc } from '../../../../api/trpc';
import { RbacObject } from '../../../../export-types/cleaned-types';
import { useRbac } from '../../../common/Rbac';
import { DatePicker } from 'rsuite';
import moment from 'moment';
import useSWRMutation from 'swr/mutation';
import useSWR from 'swr';

const Cell: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  return (
    <div
      className={
        'col-4 full-text border-end   text-center py-2 px-2 align-items-center '
      }
      style={{ minWidth: '300px', height: '50px' }}
    >
      {children}
    </div>
  );
};

const Row: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  return (
    <div
      className={classNames(
        'position-relative row g-0 bg-white border-bottom  p-0   h-100 w-100',
        tableStyle.tableRow,
      )}
    >
      {children}
    </div>
  );
};

const ButtonForUpdate = ({ onClick }: { onClick: () => void }) => {
  return (
    <button type="button" className="btn btn-success" onClick={onClick}>
      <i className="bi bi-check" />
    </button>
  );
};

type Form = {
  bonusPercent: number;
  bonusExpirationDate: Date;
  purchaseOrderThreshold: number;
  deliveryOrderThreshold: number | null;
};

export const PromotionBonusTab = () => {
  const { hasPermission } = useRbac();
  const [editedFields, setEditedFields] = React.useState<Array<string>>([]);

  const { data: settings, mutate } = useSWR(
    ['promotionBonusSettings.get'],
    () => trpc.promotionBonusSettings.get.query(),
  );
  const { trigger: updateSettings } = useSWRMutation(
    ['promotionBonusSettings.update'],
    (_k, { arg }: { arg: RouterInput['promotionBonusSettings']['update'] }) =>
      trpc.promotionBonusSettings.update.mutate(arg),
  );
  const { handleSubmit, control, getValues, reset } = useForm<Form>({});
  const [isEditing, setIsEditing] = React.useState(false);
  const canEdit = hasPermission(RbacObject.Promotion, 'write');

  const handleUpdate = useCallback(
    async (data: Form) => {
      const processedData = {
        ...data,
        deliveryOrderThreshold: data.deliveryOrderThreshold || undefined,
      };

      await updateSettings(processedData).then(() => {
        toast.success('Настройки обновлены');
      });
      setEditedFields([]);
    },
    [updateSettings],
  );

  useEffect(() => {
    if (settings) {
      reset({ ...settings });
    }
  }, [reset, settings]);

  return (
    <div>
      <form
        id="promotionBonusTab"
        onSubmit={handleSubmit(async (data) => {
          await handleUpdate(data);
          setIsEditing(false);
          mutate();
        })}
      >
        <div
          className={classNames(
            'mb-3 border w-fit-content',
            tableStyle.rowContainer,
          )}
        >
          <Row>
            <Cell>
              <span className={'fw-600'}>Название</span>
            </Cell>
            <Cell>
              <span className={'fw-600'}>Ед. измерения</span>
            </Cell>
            <Cell>
              <span className={'fw-600'}>Показатель</span>
            </Cell>
          </Row>
          <Row>
            <Cell>Процент оплаты бонусами</Cell>
            <Cell>%</Cell>

            <Cell>
              <Controller
                name="bonusPercent"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <input
                          className="form-control"
                          type="number"
                          min={0.01}
                          step={0.01}
                          defaultValue={field.value}
                          onChange={(e) => {
                            setEditedFields([...editedFields, field.name]);
                            field.onChange(+e.target.value);
                          }}
                        />
                      ) : (
                        field.value
                      )}
                      {editedFields.includes(field.name) && (
                        <ButtonForUpdate
                          onClick={() => handleUpdate(getValues())}
                        />
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>
          </Row>
          <Row>
            <Cell>Срок сгорания бонусов</Cell>
            <Cell>Дата</Cell>
            <Cell>
              <Controller
                name="bonusExpirationDate"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <>
                          <DatePicker
                            className={'w-100'}
                            format={'dd.MM.yyyy'}
                            isoWeek={true}
                            onChange={(v) => {
                              if (v === null) {
                                field.onChange(null);
                              } else {
                                field.onChange(v);
                              }
                            }}
                            value={field.value ? field.value : undefined}
                          />
                          {editedFields.includes(field.name) && (
                            <ButtonForUpdate
                              onClick={() => handleUpdate(getValues())}
                            />
                          )}
                        </>
                      ) : (
                        <div
                          className={classNames(
                            moment().isAfter(field.value) && 'color-red',
                          )}
                        >
                          {moment(field.value).format('DD.MM.YYYY')}
                        </div>
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>{' '}
          </Row>
          <Row>
            <Cell>
              <span>Минимальный порог для заказов</span>
            </Cell>
            <Cell>
              <span>руб.</span>
            </Cell>
            <Cell>
              <Controller
                name="purchaseOrderThreshold"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <input
                          className="form-control"
                          type="number"
                          min={0.01}
                          step={0.01}
                          defaultValue={field.value}
                          onChange={(e) => {
                            setEditedFields([...editedFields, field.name]);
                            field.onChange(+e.target.value);
                          }}
                        />
                      ) : (
                        field.value
                      )}
                      {editedFields.includes(field.name) && (
                        <ButtonForUpdate
                          onClick={() => handleUpdate(getValues())}
                        />
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>
          </Row>
          <Row>
            <Cell>
              <span>Минимальный порог для доставок</span>
            </Cell>
            <Cell>
              <span>руб.</span>
            </Cell>
            <Cell>
              <Controller
                name="deliveryOrderThreshold"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <input
                          className="form-control"
                          type="number"
                          min={0.01}
                          step={0.01}
                          defaultValue={field.value || ''}
                          onChange={(e) => {
                            setEditedFields([...editedFields, field.name]);
                            field.onChange(+e.target.value);
                          }}
                        />
                      ) : (
                        field.value
                      )}
                      {editedFields.includes(field.name) && (
                        <ButtonForUpdate
                          onClick={() => handleUpdate(getValues())}
                        />
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>
          </Row>
        </div>
        {canEdit ? (
          <>
            {!isEditing && (
              <button
                type="button"
                className="btn btn-light mb-2 mleft-auto"
                onClick={() => {
                  setIsEditing(true);
                }}
              >
                Изменить
              </button>
            )}
            {isEditing && (
              <button type="submit" className="btn btn-success mb-2 mleft-auto">
                Сохранить
              </button>
            )}
          </>
        ) : null}
      </form>
    </div>
  );
};
